import { getFactionStoredWrestlers } from './factionWrestlers';
import { getEffectiveFactionMemberStatus } from './factionLineupRole';

export const getFactionScoreStartDate = (faction, roster = null) => {
  const value = roster?.dataInizioPunteggio || faction?.dataInizioPunteggio;
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const hasFactionScoringStarted = (
  faction,
  referenceDate = new Date(),
  roster = null
) => {
  const startDate = getFactionScoreStartDate(faction, roster);
  if (!startDate) return true;
  return referenceDate.getTime() >= startDate.getTime();
};

export const applyFactionMemberRoleToPoints = (points = 0, status = null) => {
  if (status === 'Backstage') return 0;
  if (status === 'Main_Eventer') return points * 2;
  return points;
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const hasExplicitMainEventerSetting = (source) =>
  source && Object.prototype.hasOwnProperty.call(source, 'hasMainEventer');

const hasLegacyMainEventerEvidence = (faction) => {
  const snapshots = [
    faction?.currentLineup,
    faction?.lineup,
    ...(Array.isArray(faction?.lineupHistory) ? faction.lineupHistory : []),
  ].filter(Boolean);

  return snapshots.some(
    (snapshot) =>
      Boolean(snapshot?.champion || snapshot?.championId) ||
      Boolean(snapshot?.mainEventerAssignedAt) ||
      Boolean(snapshot?.mainEventerExpiresAt)
  );
};

export const rosterRequiresMainEventer = (roster, faction = null) => {
  const source = roster || faction || {};
  if (hasExplicitMainEventerSetting(source)) {
    return source.hasMainEventer !== false;
  }

  if (roster) return true;
  return hasLegacyMainEventerEvidence(faction);
};

export const rosterRequiresLineup = (roster, faction = null) => {
  const source = roster || faction || {};
  const hasBackstage = Boolean(source.hasBackstage) || Number(source.backstageCount || 0) > 0;
  const hasMainEventer = rosterRequiresMainEventer(roster, faction);

  return hasBackstage || hasMainEventer;
};

export const getFactionMainEventerStatus = ({
  faction,
  roster = null,
  referenceDate = new Date(),
  warningDays = 3,
}) => {
  if (!faction || !rosterRequiresMainEventer(roster, faction)) return null;

  const activeLineup = faction.currentLineup || faction.lineup;
  const champion = activeLineup?.champion || activeLineup?.championId || null;
  const expiresAt = toDate(activeLineup?.mainEventerExpiresAt);

  if (!champion || !expiresAt || Number.isNaN(expiresAt.getTime())) return null;

  const today = toDate(referenceDate) || new Date();
  today.setHours(0, 0, 0, 0);

  const expiryDay = new Date(expiresAt);
  expiryDay.setHours(0, 0, 0, 0);

  const diffInDays = Math.ceil(
    (expiryDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffInDays < 0) {
    return {
      kind: 'expired',
      label: 'Main eventer scaduto!',
      expiresAt,
      diffInDays,
    };
  }

  if (diffInDays <= warningDays) {
    return {
      kind: 'expiring',
      label: 'Main eventer in scadenza!',
      expiresAt,
      diffInDays,
    };
  }

  return null;
};

const normalizeLineupSnapshot = (snapshot) => {
  if (!snapshot) return null;

  const champion = snapshot.champion || snapshot.championId || null;
  const inRing = snapshot.inRing || snapshot.inRingIds || [];
  const backstage = snapshot.backstage || snapshot.backstageIds || [];
  const hasAssignedWrestlers =
    Boolean(champion) ||
    (Array.isArray(inRing) && inRing.filter(Boolean).length > 0) ||
    (Array.isArray(backstage) && backstage.filter(Boolean).length > 0);

  if (!hasAssignedWrestlers) return null;

  return {
    effectiveFrom: toDate(snapshot.effectiveFrom),
    champion,
    inRing,
    backstage,
  };
};

export const hasFactionLineupAtDate = (faction, referenceDate = new Date()) => {
  const targetDate = toDate(referenceDate) || new Date();
  const snapshots = [
    ...(Array.isArray(faction?.lineupHistory) ? faction.lineupHistory : []),
    faction?.currentLineup,
    faction?.lineup,
  ]
    .map(normalizeLineupSnapshot)
    .filter(Boolean);

  return snapshots.some(
    (snapshot) => !snapshot.effectiveFrom || snapshot.effectiveFrom <= targetDate
  );
};

export const shouldBlockFactionScoringForMissingLineup = ({
  faction,
  roster = null,
  referenceDate = new Date(),
}) => rosterRequiresLineup(roster, faction) && !hasFactionLineupAtDate(faction, referenceDate);

export const shouldZeroFactionMemberPointsForMissingLineupRole = ({
  faction,
  roster = null,
  wrestlerId = null,
  effectiveStatus = null,
}) => Boolean(wrestlerId) && rosterRequiresLineup(roster, faction) && !effectiveStatus;

export const getFactionMemberPoints = ({
  faction,
  roster = null,
  wrestlerId = null,
  currentScore = 0,
  startScore = 0,
  status = null,
  referenceDate = new Date(),
}) => {
  if (!hasFactionScoringStarted(faction, referenceDate, roster)) return 0;
  if (shouldBlockFactionScoringForMissingLineup({ faction, roster, referenceDate })) return 0;

  const effectiveStatus = getEffectiveFactionMemberStatus({
    faction,
    wrestlerId,
    fallbackStatus: status,
    referenceDate,
  });

  if (
    shouldZeroFactionMemberPointsForMissingLineupRole({
      faction,
      roster,
      wrestlerId,
      effectiveStatus,
    })
  ) {
    return 0;
  }

  return applyFactionMemberRoleToPoints(
    (currentScore || 0) - (startScore || 0),
    effectiveStatus
  );
};

export const getFactionDisplayPoints = ({
  faction,
  roster = null,
  wrestlerId = null,
  currentScore = 0,
  startScore = 0,
  status = null,
  referenceDate = new Date(),
}) => {
  if (!hasFactionScoringStarted(faction, referenceDate, roster)) return 0;
  if (shouldBlockFactionScoringForMissingLineup({ faction, roster, referenceDate })) return 0;
  const diff = (currentScore || 0) - (startScore || 0);
  const effectiveStatus = getEffectiveFactionMemberStatus({
    faction,
    wrestlerId,
    fallbackStatus: status,
    referenceDate,
  });
  if (
    shouldZeroFactionMemberPointsForMissingLineupRole({
      faction,
      roster,
      wrestlerId,
      effectiveStatus,
    })
  ) {
    return 0;
  }
  return applyFactionMemberRoleToPoints(
    (startScore || 0) === 0 ? (currentScore || 0) : diff,
    effectiveStatus
  );
};

export const getFactionMemberContribution = ({
  faction,
  roster = null,
  wrestlerId = null,
  currentScore = 0,
  startScore = 0,
  accumulatedPoints = 0,
  status = null,
  referenceDate = new Date(),
}) => {
  if (!hasFactionScoringStarted(faction, referenceDate, roster)) return 0;
  if (shouldBlockFactionScoringForMissingLineup({ faction, roster, referenceDate })) return 0;

  return (
    (accumulatedPoints || 0) +
    getFactionMemberPoints({
      faction,
      roster,
      wrestlerId,
      currentScore,
      startScore,
      status,
      referenceDate,
    })
  );
};

export const getFactionMemberSwapSummary = ({
  faction,
  roster = null,
  wrestlerId = null,
  currentScore = 0,
  startScore = 0,
  accumulatedPoints = 0,
  status = null,
  referenceDate = new Date(),
}) => {
  const currentPoints = getFactionMemberPoints({
    faction,
    roster,
    wrestlerId,
    currentScore,
    startScore,
    status,
    referenceDate,
  });

  const totalPoints = getFactionMemberContribution({
    faction,
    roster,
    wrestlerId,
    currentScore,
    startScore,
    accumulatedPoints,
    status,
    referenceDate,
  });

  return {
    currentPoints,
    totalPoints,
    savedPoints: Math.max(totalPoints, 0),
  };
};

export const calculateFactionTotalScore = ({
  faction,
  roster = null,
  members,
  wrestlerScoresById = {},
  referenceDate = new Date(),
}) => {
  if (!hasFactionScoringStarted(faction, referenceDate, roster)) return 0;
  if (shouldBlockFactionScoringForMissingLineup({ faction, roster, referenceDate })) return 0;

  const factionMembers = Array.isArray(members) ? members : getFactionStoredWrestlers(faction);
  const memberPoints = factionMembers.reduce((sum, member) => {
    const currentScore = wrestlerScoresById[member.id] ?? member.score ?? member.currentScore ?? 0;
    return sum + getFactionMemberPoints({
      faction,
      roster,
      wrestlerId: member.id,
      currentScore,
      startScore: member.startScore || 0,
      status: member.status || null,
      referenceDate,
    });
  }, 0);

  return (faction?.accumulatedPoints || 0) + memberPoints - (faction?.malus || 0);
};

export const settleFactionMembersForStatusChanges = ({
  faction,
  roster = null,
  members = [],
  statusByWrestlerId = {},
  wrestlerScoresById = {},
  referenceDate = new Date(),
}) => {
  const hasExistingLineup = hasFactionLineupAtDate(faction, referenceDate);
  let accumulatedPointsDelta = 0;

  const updatedMembers = members.map((member) => {
    const id = member.id || member.wrestlerId;
    const hasNextStatus = Object.prototype.hasOwnProperty.call(statusByWrestlerId, id);
    const previousStatus = member.status || null;
    const nextStatus = hasNextStatus ? statusByWrestlerId[id] : previousStatus;
    const currentScore = wrestlerScoresById[id] ?? member.score ?? member.currentScore ?? 0;
    const shouldSettleScore = previousStatus !== nextStatus || !hasExistingLineup;

    if (!hasExistingLineup) {
      return {
        ...member,
        id,
        wrestlerId: id,
        status: nextStatus,
        startScore: currentScore,
        currentScore,
        accumulatedPoints: member.accumulatedPoints ?? 0,
      };
    }

    if (!shouldSettleScore) {
      return {
        ...member,
        id,
        wrestlerId: id,
        status: nextStatus,
      };
    }

    const frozenPoints = getFactionMemberPoints({
      faction,
      roster,
      currentScore,
      startScore: member.startScore ?? 0,
      status: previousStatus,
      referenceDate,
    });

    accumulatedPointsDelta += frozenPoints;

    return {
      ...member,
      id,
      wrestlerId: id,
      status: nextStatus,
      startScore: currentScore,
      accumulatedPoints: (member.accumulatedPoints ?? 0) + frozenPoints,
      currentScore,
    };
  });

  return {
    members: updatedMembers,
    accumulatedPointsDelta,
  };
};
